import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSettings } from '@/hooks/useSettings'
import { cn, formatPrice } from '@/lib/utils'
import { X } from 'lucide-react'

const ROTATE_INTERVAL_MS = 5000

interface AnnouncementBarProps {
  show?: boolean
  onDismiss?: () => void
}

export function AnnouncementBar({ show = true, onDismiss }: AnnouncementBarProps) {
  const { storefront, content_visibility, promo_modules } = useSettings()

  const banner = promo_modules?.banner
  const bannerEnabled = banner?.enabled === true && (banner?.text?.trim() ?? '').length > 0

  const threshold = storefront?.freeShippingThresholdKobo
  const fallback = useMemo(() => {
    return threshold
      ? `Free shipping over ${formatPrice(threshold)} · 2ml samples with every order · refills now available`
      : 'Free shipping over ₦30,000 · 2ml samples with every order · refills now available'
  }, [threshold])

  // Support both a single legacy text and a rotating message list. The list
  // is capped at 5 in the UI; admins can store more, but only the first 5 are
  // shown. If the admin promo banner is enabled, it overrides the legacy list.
  const messages = useMemo(() => {
    if (bannerEnabled) return [banner!.text!.trim()]
    const list = storefront?.announcementMessages?.filter(Boolean)
    if (list && list.length > 0) return list.slice(0, 5)
    return [storefront?.announcementText ?? fallback]
  }, [bannerEnabled, banner, storefront?.announcementMessages, storefront?.announcementText, fallback])

  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (messages.length <= 1 || paused) return
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % messages.length)
    }, ROTATE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [messages.length, paused])

  if (content_visibility?.showAnnouncement === false) return null
  if (!show) return null

  const barBackground = banner?.background?.trim() || '#e85d04'
  const isStrip = banner?.style === 'strip'
  const link = banner?.link?.trim()

  const hoverHandlers = {
    onMouseEnter: () => setPaused(true),
    onMouseLeave: () => setPaused(false),
    onTouchStart: () => setPaused(true),
    onTouchEnd: () => setPaused(false),
  }

  const inner = (
    <>
      <div className="h-[1.25em] min-w-0 flex-1 overflow-hidden">
        <div
          className="transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
          style={{ transform: `translateY(${-index * 100}%)` }}
        >
          {messages.map((message, i) => (
            <div
              key={i}
              className={cn(
                'h-[1.25em] leading-[1.25em]',
                isStrip ? 'text-center' : 'whitespace-nowrap'
              )}
              aria-hidden={i !== index}
            >
              {message}
            </div>
          ))}
        </div>
      </div>

      {onDismiss && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss announcement"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-white/70 transition-colors hover:text-white"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </>
  )

  const wrapperClass = cn(
    'relative inline-flex min-w-0 max-w-full items-center gap-3 py-2 pl-5 pr-10 text-xs font-medium normal-case tracking-[0.2em] text-white transition-colors',
    isStrip ? 'w-full justify-center rounded-none' : 'rounded-full'
  )

  if (isStrip) {
    return (
      <div className="w-full" style={{ backgroundColor: barBackground }}>
        {link ? (
          <Link to={link} className={wrapperClass} {...hoverHandlers}>{inner}</Link>
        ) : (
          <div className={wrapperClass} {...hoverHandlers}>{inner}</div>
        )}
      </div>
    )
  }

  return link ? (
    <Link to={link} className={wrapperClass} style={{ backgroundColor: barBackground }} {...hoverHandlers}>{inner}</Link>
  ) : (
    <div className={wrapperClass} style={{ backgroundColor: barBackground }} {...hoverHandlers}>{inner}</div>
  )
}
